import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Mail, 
  FileText, 
  Calendar, 
  CheckCircle, 
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  Check,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';
import { API_BASE_URL } from '../../utils/api';
import { Link } from 'react-router-dom';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  data: any;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

interface NotificationStats {
  unread: number;
}

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1
  });
  const [stats, setStats] = useState<NotificationStats>({ unread: 0 });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([]);
  const [detailNotification, setDetailNotification] = useState<Notification | null>(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('skillseek_admin_token');

      // Build query params robustly: only include filters when not 'all'
      const params = new URLSearchParams();
      params.set('page', String(pagination.page));
      params.set('limit', String(pagination.limit));
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (readFilter !== 'all') params.set('is_read', readFilter === 'read' ? 'true' : 'false');

      const response = await fetch(`${API_BASE_URL}/notifications/admin?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotifications(data.notifications);
        setPagination(data.pagination);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (id: number) => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === id 
              ? { ...notification, is_read: true } 
              : notification
          )
        );
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/notifications/admin/read-all`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, is_read: true }))
        );
        setStats(prev => ({ ...prev, unread: 0 }));
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Handle notification selection
  const handleSelectNotification = (id: number) => {
    setSelectedNotifications(prev => 
      prev.includes(id) 
        ? prev.filter(notificationId => notificationId !== id)
        : [...prev, id]
    );
  };

  // Select all notifications
  const handleSelectAll = () => {
    if (selectedNotifications.length === notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map(notification => notification.id));
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return <Calendar size={20} className="notification-icon booking" />;
      case 'contact':
        return <Mail size={20} className="notification-icon contact" />;
      case 'system':
        return <AlertTriangle size={20} className="notification-icon system" />;
      default:
        return <FileText size={20} className="notification-icon general" />;
    }
  };

  // Get notification link
  const getNotificationLink = (notification: Notification) => {
    if (!notification.data) return '#';
    
    try {
      const data = typeof notification.data === 'string' 
        ? JSON.parse(notification.data) 
        : notification.data;
      
      if (data.type === 'service_inquiry') {
        return '/admin/inquiries';
      }
      // For contact_form and direct_message (and any other unhandled types), show details modal
      return '#';
    } catch (e) {
      return '#';
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchNotifications();
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesType = typeFilter === 'all' || notification.type === typeFilter;
    const matchesRead = readFilter === 'all' || (readFilter === 'read' ? notification.is_read : !notification.is_read);
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) || notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesRead && matchesSearch;
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Reset filters
  const resetFilters = () => {
    setTypeFilter('all');
    setReadFilter('all');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Fetch notifications on mount and when filters change
  useEffect(() => {
    fetchNotifications();
  }, [pagination.page, typeFilter, readFilter]);

  return (
    <AdminLayout title="Notifications" subtitle="Manage system notifications">
      <div className="admin-card" style={{ marginBottom: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            {/* Search */}
            <div style={{ position: 'relative', minWidth: '300px' }}>
              <input
                type="text"
                placeholder="Search notifications..."
                className="form-control"
                value={searchTerm}
                onChange={(e) => handleSearch(e)}
                style={{ paddingLeft: '40px' }}
              />
              <Search size={18} style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: 'var(--text-medium)'
              }} />
            </div>

            {/* Filters */}
            <select 
              className="form-control" 
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ minWidth: '120px' }}
            >
              <option value="all">All Types</option>
              <option value="booking">Booking</option>
              <option value="contact">Contact</option>
              <option value="system">System</option>
              <option value="general">General</option>
            </select>

            <select 
              className="form-control" 
              value={readFilter}
              onChange={(e) => setReadFilter(e.target.value)}
              style={{ minWidth: '120px' }}
            >
              <option value="all">All Status</option>
              <option value="read">Read</option>
              <option value="unread">Unread</option>
            </select>

            {/* <button 
              className="btn-secondary"
              onClick={applyFilters}
              title="Apply Filters"
            >
              <Filter size={18} />
              Apply
            </button> */}

            <button 
              className="btn-secondary"
              onClick={resetFilters}
              title="Reset Filters"
            >
              <RefreshCw size={18} />
              Reset
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {selectedNotifications.length > 0 && (
              <button 
                className="btn-primary"
                onClick={() => {
                  // Mark selected notifications as read
                  selectedNotifications.forEach(id => markAsRead(id));
                  setSelectedNotifications([]);
                }}
              >
                <Check size={18} />
                Mark Selected as Read ({selectedNotifications.length})
              </button>
            )}
            {stats.unread > 0 && (
              <button 
                className="btn-primary"
                onClick={markAllAsRead}
              >
                <CheckCircle size={18} />
                Mark All as Read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stat-cards" style={{ marginBottom: '25px' }}>
        <div className="stat-card">
          <div className="stat-icon">
            <Bell size={24} />
          </div>
          <div className="stat-value">{pagination.totalItems}</div>
          <div className="stat-label">Total Notifications</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-value">{stats.unread}</div>
          <div className="stat-label">Unread Notifications</div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="admin-card">
        <div className="card-header">
          <div className="card-title">Notifications</div>
          <div style={{ fontSize: '14px', color: 'var(--text-medium)' }}>
            Showing {notifications.length} of {pagination.totalItems} notifications
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner" style={{ textAlign: 'center', padding: '50px' }}>
            <div className="spinner"></div>
          
          </div>
        ) : notifications.length > 0 ? (
          <div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedNotifications.length === notifications.length && notifications.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ width: '50px' }}>Type</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredNotifications.map(notification => (
                  <tr 
                    key={notification.id}
                    className={notification.is_read ? '' : 'unread-row'}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification.id)}
                        onChange={() => handleSelectNotification(notification.id)}
                      />
                    </td>
                    <td>
                      {getNotificationIcon(notification.type)}
                    </td>
                    <td>
                      <div style={{ fontWeight: notification.is_read ? '400' : '600' }}>
                        {notification.title}
                      </div>
                    </td>
                    <td>
                      <div style={{ 
                        maxWidth: '400px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        fontWeight: notification.is_read ? '400' : '500'
                      }}>
                        {notification.message}
                      </div>
                    </td>
                    <td>
                      {formatDate(notification.created_at)}
                    </td>
                    <td>
                      <span className={`badge ${notification.is_read ? 'badge-secondary' : 'badge-info'}`}>
                        {notification.is_read ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {(() => {
                          const link = getNotificationLink(notification);
                          if (link === '#') {
                            return (
                              <button
                                className="btn-secondary"
                                style={{ padding: '6px', minWidth: 'auto' }}
                                title="View Details"
                                onClick={() => setDetailNotification(notification)}
                              >
                                <FileText size={14} />
                              </button>
                            );
                          }
                          return (
                            <Link 
                              to={link}
                              className="btn-secondary" 
                              style={{ padding: '6px', minWidth: 'auto' }}
                              title="View Details"
                            >
                              <FileText size={14} />
                            </Link>
                          );
                        })()}
                        {!notification.is_read && (
                          <button 
                            className="btn-secondary" 
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => markAsRead(notification.id)}
                            title="Mark as Read"
                          >
                            <Check size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginTop: '20px',
              padding: '20px 0'
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-medium)' }}>
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn-secondary" 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state" style={{ padding: '50px', textAlign: 'center' }}>
            <Bell size={48} style={{ opacity: 0.3, marginBottom: '15px' }} />
            <h3>No Notifications Found</h3>
            <p>You don't have any notifications matching your filters.</p>
            {(typeFilter !== 'all' || readFilter !== 'all' || searchTerm) && (
              <button 
                className="btn-secondary"
                onClick={resetFilters}
              >
                <RefreshCw size={18} />
                Reset Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add CSS as a regular style tag without jsx attribute */}
      <style>{`
        .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-card { background: #fff; border-radius: 8px; width: 90%; max-width: 560px; padding: 20px; }
        .notification-icon {
          padding: 8px;
          border-radius: 50%;
          background-color: #f0f0f0;
        }
        
        .notification-icon.booking {
          color: #4f46e5;
          background-color: rgba(79, 70, 229, 0.1);
        }
        
        .notification-icon.contact {
          color: #10b981;
          background-color: rgba(16, 185, 129, 0.1);
        }
        
        .notification-icon.system {
          color: #f59e0b;
          background-color: rgba(245, 158, 11, 0.1);
        }
        
        .notification-icon.general {
          color: #6b7280;
          background-color: rgba(107, 114, 128, 0.1);
        }
        
        .unread-row {
          background-color: #f0f7ff;
        }
        
        .unread-row:hover {
          background-color: #e6f2ff;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(0, 0, 0, 0.1);
          border-top-color: #4f46e5;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {detailNotification && (
        <div className="modal-backdrop" onClick={() => setDetailNotification(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="card-header" style={{ marginBottom: 12 }}>
              <div className="card-title">Notification Details</div>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <div><strong>Title:</strong> {detailNotification.title}</div>
              <div><strong>Message:</strong> {detailNotification.message}</div>
              <div><strong>Type:</strong> {detailNotification.type}</div>
              <div><strong>Date:</strong> {new Date(detailNotification.created_at).toLocaleString()}</div>
              {(() => {
                try {
                  const data = typeof detailNotification.data === 'string' ? JSON.parse(detailNotification.data) : detailNotification.data;
                  return data ? (
                    <div>
                      <strong>Data:</strong>
                      <pre style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, overflow: 'auto' }}>{JSON.stringify(data, null, 2)}</pre>
                    </div>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn-secondary" onClick={() => setDetailNotification(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default NotificationsPage;